import { useState, useRef } from 'react';

const FileUploader = ({ onUpload, maxFiles = 10, isUploading = false }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const allowedTypes = {
    'application/pdf': { ext: 'pdf', icon: '📄', color: 'text-red-600' },
    'application/msword': { ext: 'doc', icon: '📝', color: 'text-blue-600' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: 'docx', icon: '📝', color: 'text-blue-600' },
    'application/vnd.ms-powerpoint': { ext: 'ppt', icon: '📊', color: 'text-orange-600' },
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': { ext: 'pptx', icon: '📊', color: 'text-orange-600' },
    'application/vnd.ms-excel': { ext: 'xls', icon: '📈', color: 'text-green-600' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: 'xlsx', icon: '📈', color: 'text-green-600' },
    'text/plain': { ext: 'txt', icon: '📄', color: 'text-gray-600' }
  };

  const getFileIcon = (type) => {
    return allowedTypes[type]?.icon || '📎';
  };

  const getFileColor = (type) => {
    return allowedTypes[type]?.color || 'text-gray-600';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      // Validate file type
      if (!allowedTypes[file.type]) {
        alert(`${file.name} is not a supported file type`);
        return false;
      }
      
      // Validate file size (50MB)
      if (file.size > 50 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum size is 50MB`);
        return false;
      }
      
      return true;
    });

    if (validFiles.length === 0) return;

    // Limit number of files
    const filesToProcess = validFiles.slice(0, maxFiles);
    
    // Add to selected files
    const newFiles = filesToProcess.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Date.now() + Math.random()
    }));
    
    setSelectedFiles(prev => [...prev, ...newFiles].slice(0, maxFiles));
    
    // Call onUpload callback
    onUpload(filesToProcess);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (id) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== id));
  };

  const clearAll = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
          dragActive
            ? 'border-teal-500 bg-teal-50'
            : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'
        } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
          onChange={handleChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        
        <div className="space-y-4">
          <div className="text-6xl">
            {isUploading ? '⏳' : '📎'}
          </div>
          <div>
            <p className="text-lg font-medium text-gray-700">
              {isUploading ? 'Uploading...' : 'Drag & drop files here'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              or click to browse • PDF, DOC, PPT, XLS, TXT • Max 50MB each
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Maximum {maxFiles} attachments
            </p>
          </div>
          {!isUploading && (
            <button
              type="button"
              className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors font-medium"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose Files
            </button>
          )}
        </div>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-700">
              Selected Files ({selectedFiles.length}/{maxFiles})
            </h4>
            <button
              type="button"
              onClick={clearAll}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Clear All
            </button>
          </div>
          
          <div className="space-y-2">
            {selectedFiles.map((fileData) => (
              <div key={fileData.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                {/* File Icon */}
                <div className={`text-2xl ${getFileColor(fileData.type)}`}>
                  {getFileIcon(fileData.type)}
                </div>
                
                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate" title={fileData.name}>
                    {fileData.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(fileData.size)}
                  </p>
                </div>
                
                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => removeFile(fileData.id)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove file"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          
          {/* Total Size */}
          <div className="text-sm text-gray-500 text-center">
            Total size: {formatFileSize(selectedFiles.reduce((sum, file) => sum + file.size, 0))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;